import { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, FileText, Save, X } from 'lucide-react';
import { crmApi } from '../crmApi';
import { UserSession } from '../../../types';

interface Props { session?: UserSession | null; }

const CATEGORIAS = ['geral', 'prospeccao', 'follow_up', 'venda', 'pos_venda', 'agendamento'];

const TemplatesWhatsapp = ({ session }: Props) => {
  const [items, setItems] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [editando, setEditando] = useState<any | null>(null);
  const [novo, setNovo] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const list = await crmApi.templates.listar(session);
      setItems(list);
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setCarregando(false); }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [session]);

  const remover = async (t: any) => {
    if (!confirm(`Excluir o template "${t.nome}"?`)) return;
    try { await crmApi.templates.deletar(session, t.id); carregar(); }
    catch (e: any) { alert('Erro: ' + e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Modelos de mensagem</h3>
        <button onClick={() => setNovo(true)}
          className="px-3 py-1.5 text-sm font-medium bg-gray-900 text-white rounded-md hover:bg-gray-700 flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Novo template
        </button>
      </div>

      {carregando ? (
        <p className="text-xs text-gray-400 text-center py-8">Carregando…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <FileText className="h-10 w-10 mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">Nenhum template ainda. Crie seu primeiro modelo de mensagem.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map(t => (
            <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-4 group">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{t.nome}</p>
                  <span className="text-[10px] uppercase tracking-wide text-gray-500">{t.categoria || 'geral'}</span>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setEditando(t)} className="p-1 hover:bg-gray-100 rounded text-gray-600">
                    <Edit3 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => remover(t)} className="p-1 hover:bg-red-50 rounded text-red-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-700 whitespace-pre-wrap line-clamp-5">{t.conteudo}</p>
            </div>
          ))}
        </div>
      )}

      {(novo || editando) && (
        <TemplateModal session={session} item={editando}
          onClose={() => { setNovo(false); setEditando(null); }}
          onSaved={() => { setNovo(false); setEditando(null); carregar(); }} />
      )}
    </div>
  );
};

const TemplateModal = ({ session, item, onClose, onSaved }: { session: any; item?: any; onClose: () => void; onSaved: () => void }) => {
  const [nome, setNome] = useState(item?.nome || '');
  const [categoria, setCategoria] = useState(item?.categoria || 'geral');
  const [conteudo, setConteudo] = useState(item?.conteudo || '');
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!nome.trim() || !conteudo.trim()) { alert('Preencha nome e conteúdo'); return; }
    setSalvando(true);
    try {
      if (item) await crmApi.templates.atualizar(session, item.id, { nome, categoria, conteudo });
      else await crmApi.templates.criar(session, { nome, categoria, conteudo });
      onSaved();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-semibold">{item ? 'Editar' : 'Novo'} template</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs text-gray-600">Nome</label>
            <input value={nome} onChange={e => setNome(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Categoria</label>
            <select value={categoria} onChange={e => setCategoria(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white">
              {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Conteúdo</label>
            <textarea value={conteudo} onChange={e => setConteudo(e.target.value)} rows={8}
              placeholder="Olá {{nome}}, tudo bem? ..."
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md resize-none font-mono" />
            <p className="text-[11px] text-gray-500 mt-1">Variáveis disponíveis: <code>{'{{nome}}'}</code>, <code>{'{{empresa}}'}</code></p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm bg-gray-100 rounded-md hover:bg-gray-200">Cancelar</button>
          <button onClick={salvar} disabled={salvando}
            className="px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-700 disabled:opacity-40 flex items-center gap-1.5">
            <Save className="h-4 w-4" /> {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatesWhatsapp;
